import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { generateCounselorCode } from './counselorService';
import { verifyCounselorCode, createCouple, generateCoupleCode } from './coupleService';

/**
 * Register a new user (couple or counselor)
 */
export const signUp = async (email, password, userData) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // If counselor, generate a unique counselor code
    if (userData.role === 'counselor') {
      userData.counselorCode = generateCounselorCode();
    }
    
    // Save user profile to Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email,
      ...userData,
      createdAt: new Date().toISOString()
    });
    
    return { 
      user, 
      counselorCode: userData.counselorCode, 
      error: null 
    };
  } catch (error) {
    return { user: null, counselorCode: null, error: error.message };
  }
};

/**
 * Register a couple (Partner 1 or Partner 2)
 */
export const registerCouple = async (email, password, name, code, partnerNumber) => {
  const trimmedEmail = email.trim();
  const trimmedPassword = password.trim();
  const trimmedName = name.trim();
  const trimmedCode = code.trim().toUpperCase();
  
  console.log('authService: Registering couple');
  console.log('Email:', trimmedEmail);
  console.log('Code:', trimmedCode);
  console.log('Partner number:', partnerNumber);
  
  try {
    // 1. Create Firebase Auth account
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      trimmedEmail,
      trimmedPassword
    );
    
    const user = userCredential.user;
    console.log('✅ Auth account created:', user.uid);
    
    // 2. Determine if this is Partner 1 or Partner 2 based on code format
    const isCoupleCode = trimmedCode.includes('-');
    const isPartner1 = partnerNumber === 1 || !isCoupleCode;
    
    console.log('Is couple code?', isCoupleCode);
    console.log('Is Partner 1?', isPartner1);
    
    let coupleCode = null;
    
    if (isPartner1) {
      // PARTNER 1 FLOW
      console.log('=== PARTNER 1 REGISTRATION ===');
      
      // Verify counselor code
      const verification = await verifyCounselorCode(trimmedCode);
      if (!verification.valid) {
        // Delete the auth user we just created
        await user.delete();
        throw new Error('Invalid counselor code. Please check with your counselor.');
      }
      
      console.log('✅ Counselor verified:', verification.counselorName);
      
      // Generate couple code
      coupleCode = await generateCoupleCode(trimmedCode);
      console.log('✅ Generated couple code:', coupleCode);
      
      // Save Partner 1 profile
      await setDoc(doc(db, 'users', user.uid), {
        userId: user.uid,
        email: trimmedEmail,
        name: trimmedName,
        role: 'couple',
        counselorId: verification.counselorId,
        counselorCode: trimmedCode,
        coupleCode: coupleCode,
        partnerNumber: 1,
        approvalStatus: 'waiting_for_partner',
        createdAt: new Date().toISOString()
      });
      
      console.log('✅ Partner 1 profile saved with couple code');
      
      // Verify it was saved
      const verifyDoc = await getDoc(doc(db, 'users', user.uid));
      if (!verifyDoc.data().coupleCode) {
        console.error('❌ Couple code not saved!');
        throw new Error('Failed to save couple code');
      }
      
      console.log('✅ Verified couple code in database:', verifyDoc.data().coupleCode);
      
    } else {
      // PARTNER 2 FLOW
      console.log('=== PARTNER 2 REGISTRATION ===');
      
      coupleCode = trimmedCode;
      const counselorCode = coupleCode.split('-')[0];
      
      console.log('Couple code:', coupleCode);
      console.log('Extracted counselor code:', counselorCode);
      
      // Verify counselor exists
      const verification = await verifyCounselorCode(counselorCode);
      if (!verification.valid) {
        await user.delete();
        throw new Error('Invalid couple code - counselor not found.');
      }
      
      // Find Partner 1
      const partner1Query = query(
        collection(db, 'users'),
        where('coupleCode', '==', coupleCode),
        where('role', '==', 'couple')
      );
      
      const partner1Snapshot = await getDocs(partner1Query);
      
      if (partner1Snapshot.empty) {
        await user.delete();
        throw new Error('Invalid couple code - Partner 1 not found. Please verify the code with your partner.');
      }
      
      const partner1Data = partner1Snapshot.docs[0].data();
      console.log('✅ Found Partner 1:', partner1Data.name);
      
      // Save Partner 2 profile
      await setDoc(doc(db, 'users', user.uid), {
        userId: user.uid,
        email: trimmedEmail,
        name: trimmedName,
        role: 'couple',
        counselorId: verification.counselorId,
        counselorCode: counselorCode,
        coupleCode: coupleCode,
        partnerNumber: 2,
        approvalStatus: 'pending',
        createdAt: new Date().toISOString()
      });
      
      console.log('✅ Partner 2 profile saved');
      
      // Create couple document
      const coupleResult = await createCouple(
        partner1Data.userId,
        user.uid,
        partner1Data.name,
        trimmedName,
        verification.counselorId,
        counselorCode
      );
      
      if (!coupleResult.success) {
        throw new Error('Failed to create couple document');
      }
      
      console.log('✅ Couple document created:', coupleResult.coupleId);
    }
    
    return {
      user: user,
      coupleCode: coupleCode,
      error: null
    };
    
  } catch (error) {
    console.error('authService: Registration error:', error);
    return {
      user: null,
      coupleCode: null,
      error: error.message
    };
  }
};

/**
 * Sign in with Google (for counselors)
 */
export const signInWithGoogle = async (role = 'counselor') => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    // Check if user already exists in Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      // New user - create profile
      const userData = {
        email: user.email,
        name: user.displayName || 'User',
        role: role,
        createdAt: new Date().toISOString()
      };
      
      // Generate counselor code if counselor
      if (role === 'counselor') {
        userData.counselorCode = generateCounselorCode();
      }
      
      await setDoc(doc(db, 'users', user.uid), userData);
      
      return { 
        user, 
        counselorCode: userData.counselorCode,
        isNewUser: true,
        error: null 
      };
    }
    
    // Existing user
    const existingData = userDoc.data();
    return { 
      user, 
      counselorCode: existingData.counselorCode,
      isNewUser: false,
      error: null 
    };
  } catch (error) {
    return { 
      user: null, 
      counselorCode: null,
      isNewUser: false,
      error: error.message 
    };
  }
};

/**
 * Sign in existing user
 */
export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

/**
 * Sign out current user
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true, error: null };
  } catch (error) {
    let errorMessage = 'Failed to send reset email. Please try again.';
    
    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email address.';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Please enter a valid email address.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many attempts. Please try again later.';
    }
    
    return { success: false, error: errorMessage };
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get user role from Firestore
 */
export const getUserRole = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data().role;
    }
    return null;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};